import { useMemo } from "react";
import { useThemeContext } from "@/react/providers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";
import { LabelSm } from "@circle-react-shared/uikit/Typography/components/Label/LabelSm";
import type { ChatButtonProps } from "../../types/ChatButtonProps";

const LIGHT = "#F7F9FA";
const DARK = "#191B1F";

export const ChatButton = ({
  label,
  sublabel,
  event,
  selected,
  setSelected,
  preferred = false,
  onClick,
  disabled = false,
}: ChatButtonProps) => {
  const { isDarkAppearance } = useThemeContext();

  const isSelected = selected === event;
  const isNoSelection = selected === null;
  const isClickable = isNoSelection && !disabled;

  const handleOnClick = () => {
    if (isNoSelection) {
      setSelected(event);
      onClick();
    }
  };

  const variantClasses =
    preferred &&
    isNoSelection &&
    "bg-[#2B2E33] dark:bg-white dark:text-primary text-black";

  const baseClasses =
    "rounded-xl border border-primary px-4 py-2 text-sm font-medium";

  const interactionClasses = classNames({
    "hover:border-secondary cursor-pointer": isClickable,
    "border-secondary": isSelected,
    "cursor-not-allowed": !isClickable,
    "w-full": Boolean(sublabel),
  });

  const isDarkBackground = useMemo(() => {
    const isDarkTheme = Boolean(isDarkAppearance);
    const isInverted = preferred && isNoSelection;
    return isInverted ? !isDarkTheme : isDarkTheme;
  }, [preferred, isNoSelection, isDarkAppearance]);

  // Icon color should be opposite of bg for contrast:
  const thunderColor = isDarkBackground ? LIGHT : DARK;

  // (Label colors currently identical on both branches; keep simple.)
  const labelColor =
    preferred && isNoSelection
      ? "text-white dark:text-gray-900"
      : "text-gray-900 dark:text-white";
  const sublabelColor = "text-disabled";

  return (
    <button
      disabled={disabled}
      type="button"
      className={classNames(baseClasses, variantClasses, interactionClasses)}
      onClick={handleOnClick}
    >
      {/* Thunder icon appears only for preferred buttons */}
      {preferred && (
        <Icon
          type="sw-thunder"
          size={16}
          className="mr-2"
          color={thunderColor}
        />
      )}

      <LabelSm weight="medium" color={labelColor}>
        {label}
      </LabelSm>

      {sublabel && (
        <LabelSm weight="medium" color={sublabelColor}>
          {" • "}
          {sublabel}
        </LabelSm>
      )}
    </button>
  );
};
